import React from 'react';
import { useCall } from '@/contexts/CallContext';
import { Button } from '@/components/ui/button';
import { Phone, PhoneCall, PhoneOff, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CallButtonProps {
  customerId: number;
  customerName: string;
  customerPhone: string | null | undefined;
  size?: 'sm' | 'default' | 'lg' | 'icon';
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link';
  showLabel?: boolean;
  className?: string;
}

/**
 * One-click call button component
 * Initiates browser-based calls through Twilio Client SDK
 * Automatically disabled during active calls
 */
export function CallButton({
  customerId,
  customerName,
  customerPhone,
  size = 'sm',
  variant = 'outline',
  showLabel = true,
  className = '',
}: CallButtonProps) {
  const { toast } = useToast();
  const callContext = useCall();

  // If call context not available (initialization), show disabled button
  if (!callContext) {
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Phone className="h-4 w-4" />
        {showLabel && size !== 'icon' && <span className="ml-2">Call</span>}
      </Button>
    );
  }

  // If Twilio is not configured, don't render the call button at all
  if (callContext.twilioConfigured === false) {
    return null;
  }

  const { makeCall, hangup, isCallInProgress, isReady, isInitializing, activeCall, error } = callContext;

  const handleCall = async () => {
    if (!customerPhone) {
      toast({
        title: 'No Phone Number',
        description: 'This customer does not have a phone number on file.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // makeCall will now handle initialization if needed
      await makeCall({
        id: customerId,
        name: customerName,
        phone: customerPhone,
      });
    } catch (error) {
      // Error handling is done in CallContext
      console.error('Call button error:', error);
    }
  };

  // Show initializing state only when actively initializing
  if (isInitializing) {
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        className={className}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        {showLabel && size !== 'icon' && <span className="ml-2">Connecting...</span>}
      </Button>
    );
  }

  // Show error state with retry option
  if (error && !isReady && !isInitializing) {
    return (
      <Button
        variant="outline"
        size={size}
        onClick={handleCall}
        className={className}
        title={error}
      >
        <PhoneOff className="h-4 w-4" />
        {showLabel && size !== 'icon' && <span className="ml-2">Retry</span>}
      </Button>
    );
  }

  // Determine if this button's customer is currently being called
  const isCallingThisCustomer = activeCall?.customerId === customerId;

  // Disable if:
  // 1. No phone number
  // 2. Another customer is being called (not this one)
  // 3. System is initializing (but not for this button's click)
  const isDisabled = !customerPhone || (isCallInProgress && !isCallingThisCustomer);

  // Show actionable "End Call" button if calling this specific customer
  if (isCallingThisCustomer) {
    return (
      <Button
        variant="destructive"
        size={size}
        onClick={hangup}
        className={className}
        title="End call"
      >
        <PhoneOff className="h-4 w-4 animate-pulse" />
        {showLabel && size !== 'icon' && <span className="ml-2">End Call</span>}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCall}
      disabled={isDisabled}
      className={className}
      title={!customerPhone ? 'No phone number' : `Call ${customerName}`}
    >
      <Phone className="h-4 w-4" />
      {showLabel && size !== 'icon' && <span className="ml-2">Call</span>}
    </Button>
  );
}
